// ABOUTME: The midnight watch behind useUtcDayChange and useLocalDay: calls back once a day of the given clock has
// ABOUTME: passed — by a timer armed at its next midnight plus a margin, and by the same check when the tab returns.

/** How one kind of day is told apart, and how far away its next midnight is. */
export interface DayClock {
  /** The day `now` falls on; two instants of one day compare equal. */
  dayOf: (now: number) => number | string
  untilNextMidnight: (now: number) => number
  /** How long after midnight the timer fires. */
  marginMs: number
}

/**
 * The timer alone is not enough: iOS freezes timers in the background, so a
 * tab that slept across midnight fires late or not at all. The visibility
 * check covers that, and re-arms the timer for the following midnight.
 *
 * `jitterMs` spreads the timer path only: every open tab of every user
 * crosses the same second otherwise. A tab coming back to the foreground
 * re-reads at once — its user is looking at it.
 *
 * Returns the function that ends the watch.
 */
export function watchDayBoundary(clock: DayClock, onDayChange: () => void, jitterMs = 0): () => void {
  let lastDay = clock.dayOf(Date.now())
  let timer: ReturnType<typeof setTimeout> | null = null

  const check = () => {
    const day = clock.dayOf(Date.now())
    if (day === lastDay) return
    lastDay = day
    onDayChange()
  }
  const arm = () => {
    if (timer !== null) clearTimeout(timer)
    timer = setTimeout(
      () => {
        check()
        arm()
      },
      clock.untilNextMidnight(Date.now()) + clock.marginMs + Math.random() * jitterMs,
    )
  }
  const onVisibilityChange = () => {
    if (document.visibilityState !== "visible") return
    check()
    arm()
  }

  arm()
  document.addEventListener("visibilitychange", onVisibilityChange)
  return () => {
    if (timer !== null) clearTimeout(timer)
    document.removeEventListener("visibilitychange", onVisibilityChange)
  }
}
